import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  ExpandMore as ExpandMoreIcon,
  Store as StoreIcon,
  Inventory as InventoryIcon,
  LocalShipping as LocalShippingIcon,
  Email as EmailIcon,
  AccessTime as AccessTimeIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';

const palette = {
  primary: '#1F3A5F',
  secondary: '#2C8C8C',
  accent: '#D6A75B',
  bg: '#F6F4EF',
  bgDark: '#0F1E2E',
  bgCard: '#FFFFFF',
  textDark: '#1a1a2e',
  textMuted: '#5a6872',
  textLight: '#E0E0E0',
  border: '#e8e4de',
  sectionAlt: '#F0EDE6',
};

function Help() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const quickStartCards = [
    {
      icon: <StoreIcon sx={{ fontSize: 40, color: palette.secondary }} />,
      title: t('help.qs_create_store'),
      subtitle: t('help.qs_create_store_sub'),
      steps: [t('help.qs_create_step1'), t('help.qs_create_step2'), t('help.qs_create_step3')],
    },
    {
      icon: <InventoryIcon sx={{ fontSize: 40, color: palette.secondary }} />,
      title: t('help.qs_add_products'),
      subtitle: t('help.qs_add_products_sub'),
      steps: [t('help.qs_product_step1'), t('help.qs_product_step2'), t('help.qs_product_step3')],
    },
    {
      icon: <LocalShippingIcon sx={{ fontSize: 40, color: palette.secondary }} />,
      title: t('help.qs_process_orders'),
      subtitle: t('help.qs_process_orders_sub'),
      steps: [t('help.qs_order_step1'), t('help.qs_order_step2'), t('help.qs_order_step3')],
    },
  ];

  const faqCategories = [
    {
      title: t('help.faq_cat_setup'),
      icon: <StoreIcon sx={{ fontSize: 22, color: palette.secondary }} />,
      faqs: [
        { question: t('help.faq_q_register'), answer: t('help.faq_a_register') },
        { question: t('help.faq_q_trial'), answer: t('help.faq_a_trial') },
        { question: t('help.faq_q_plan_change'), answer: t('help.faq_a_plan_change') },
      ],
    },
    {
      title: t('help.faq_cat_products'),
      icon: <InventoryIcon sx={{ fontSize: 22, color: palette.secondary }} />,
      faqs: [
        { question: t('help.faq_q_add_product'), answer: t('help.faq_a_add_product') },
        { question: t('help.faq_q_shopee_import'), answer: t('help.faq_a_shopee_import') },
        { question: t('help.faq_q_specs'), answer: t('help.faq_a_specs') },
      ],
    },
    {
      title: t('help.faq_cat_orders'),
      icon: <ShoppingCartIcon sx={{ fontSize: 22, color: palette.secondary }} />,
      faqs: [
        { question: t('help.faq_q_order_status'), answer: t('help.faq_a_order_status') },
        { question: t('help.faq_q_refund'), answer: t('help.faq_a_refund') },
        { question: t('help.faq_q_shipping_flow'), answer: t('help.faq_a_shipping_flow') },
      ],
    },
    {
      title: t('help.faq_cat_payment'),
      icon: <PaymentIcon sx={{ fontSize: 22, color: palette.secondary }} />,
      faqs: [
        { question: t('help.faq_q_payment_methods'), answer: t('help.faq_a_payment_methods') },
        { question: t('help.faq_q_cvs_setup'), answer: t('help.faq_a_cvs_setup') },
        { question: t('help.faq_q_shipping_fee'), answer: t('help.faq_a_shipping_fee') },
      ],
    },
    {
      title: t('help.faq_cat_account'),
      icon: <PersonIcon sx={{ fontSize: 22, color: palette.secondary }} />,
      faqs: [
        { question: t('help.faq_q_edit_store'), answer: t('help.faq_a_edit_store') },
        { question: t('help.faq_q_custom_domain'), answer: t('help.faq_a_custom_domain') },
        { question: t('help.faq_q_cancel_account'), answer: t('help.faq_a_cancel_account') },
      ],
    },
  ];

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box sx={{ bgcolor: palette.bg, minHeight: '100vh' }}>
      {/* Navbar */}
      <Box
        sx={{
          bgcolor: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${palette.border}`,
          py: 1.5,
          position: 'sticky',
          top: 0,
          zIndex: 1100,
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box
              component="img"
              src="/images/landing/logo-dark.webp"
              alt="URStore"
              sx={{ height: 36, cursor: 'pointer' }}
              onClick={() => navigate('/')}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/')}
                sx={{ color: palette.textMuted }}
              >
                {t('help.back_home')}
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate('/apply')}
                sx={{
                  bgcolor: palette.accent,
                  color: palette.bgDark,
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 2.5,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#c4963e' },
                }}
              >
                {t('help.free_store')}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Hero */}
      <Box sx={{ bgcolor: palette.bgDark, color: 'white', py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h2" fontWeight={800} sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, mb: 2 }}>
            <Box component="span" sx={{ color: palette.accent }}>{t('help.title')}</Box>
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.7, fontWeight: 400 }}>
            {t('help.subtitle')}
          </Typography>
        </Container>
      </Box>

      {/* Quick Start */}
      <Box sx={{ bgcolor: palette.bg, py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h3"
              fontWeight={800}
              sx={{ color: palette.textDark, mb: 1, fontSize: { xs: '1.8rem', md: '2.2rem' } }}
            >
              {t('help.quick_start')}
            </Typography>
            <Typography sx={{ color: palette.textMuted, fontSize: '1.05rem' }}>
              {t('help.quick_start_sub')}
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {quickStartCards.map((card, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    bgcolor: palette.bgCard,
                    border: `1px solid ${palette.border}`,
                    borderRadius: 3,
                    height: '100%',
                    transition: 'box-shadow 0.3s, transform 0.3s',
                    '&:hover': {
                      boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ mb: 2 }}>{card.icon}</Box>
                    <Typography variant="h5" fontWeight={700} sx={{ color: palette.textDark, mb: 0.5 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: palette.textMuted, mb: 3 }}>
                      {card.subtitle}
                    </Typography>
                    <Divider sx={{ mb: 2, borderColor: palette.border }} />
                    {card.steps.map((step, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                        <Chip
                          label={i + 1}
                          size="small"
                          sx={{
                            bgcolor: palette.secondary,
                            color: 'white',
                            fontWeight: 700,
                            minWidth: 28,
                            height: 28,
                            fontSize: '0.8rem',
                          }}
                        />
                        <Typography variant="body2" sx={{ color: palette.textDark, pt: 0.3 }}>
                          {step}
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ */}
      <Box sx={{ bgcolor: palette.sectionAlt, py: { xs: 8, md: 10 } }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h3"
              fontWeight={800}
              sx={{ color: palette.textDark, mb: 1, fontSize: { xs: '1.8rem', md: '2.2rem' } }}
            >
              {t('help.faq_title')}
            </Typography>
            <Typography sx={{ color: palette.textMuted, fontSize: '1.05rem' }}>
              {t('help.faq_subtitle')}
            </Typography>
          </Box>
          {faqCategories.map((category, catIndex) => (
            <Box key={catIndex} sx={{ mb: 4 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                {category.icon}
                <Typography variant="h6" fontWeight={700} sx={{ color: palette.textDark }}>
                  {category.title}
                </Typography>
              </Stack>
              {category.faqs.map((faq, faqIndex) => {
                const panelId = `panel-${catIndex}-${faqIndex}`;
                return (
                  <Accordion
                    key={faqIndex}
                    expanded={expanded === panelId}
                    onChange={handleAccordionChange(panelId)}
                    elevation={0}
                    disableGutters
                    sx={{
                      bgcolor: palette.bgCard,
                      border: `1px solid ${palette.border}`,
                      borderRadius: '12px !important',
                      mb: 1.5,
                      '&::before': { display: 'none' },
                      overflow: 'hidden',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: palette.textMuted }} />}
                      sx={{
                        px: 3,
                        py: 0.5,
                        '& .MuiAccordionSummary-content': { my: 1.5 },
                      }}
                    >
                      <Typography fontWeight={600} sx={{ color: palette.textDark }}>
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                      <Typography sx={{ color: palette.textMuted, lineHeight: 1.8 }}>
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          ))}
        </Container>
      </Box>

      {/* Contact Support */}
      <Box sx={{ bgcolor: palette.bg, py: { xs: 8, md: 10 } }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography
              variant="h3"
              fontWeight={800}
              sx={{ color: palette.textDark, mb: 1, fontSize: { xs: '1.8rem', md: '2.2rem' } }}
            >
              {t('help.contact_support')}
            </Typography>
            <Typography sx={{ color: palette.textMuted, fontSize: '1.05rem' }}>
              {t('help.contact_support_sub')}
            </Typography>
          </Box>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} sm={4}>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: palette.bgCard,
                  border: `1px solid ${palette.border}`,
                  borderRadius: 3,
                  p: 4,
                  textAlign: 'center',
                  height: '100%',
                }}
              >
                <EmailIcon sx={{ fontSize: 36, color: palette.secondary, mb: 1.5 }} />
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: palette.textDark, mb: 0.5 }}>
                  {t('help.email_contact')}
                </Typography>
                <Typography variant="body2" sx={{ color: palette.accent, fontWeight: 600 }}>
                  service@urstore.tw
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: palette.bgCard,
                  border: `1px solid ${palette.border}`,
                  borderRadius: 3,
                  p: 4,
                  textAlign: 'center',
                  height: '100%',
                }}
              >
                <AccessTimeIcon sx={{ fontSize: 36, color: palette.secondary, mb: 1.5 }} />
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: palette.textDark, mb: 0.5 }}>
                  {t('help.support_hours')}
                </Typography>
                <Typography variant="body2" sx={{ color: palette.textMuted }}>
                  {t('help.support_hours_value')}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: palette.bgCard,
                  border: `1px solid ${palette.border}`,
                  borderRadius: 3,
                  p: 4,
                  textAlign: 'center',
                  height: '100%',
                }}
              >
                <QuestionAnswerIcon sx={{ fontSize: 36, color: palette.secondary, mb: 1.5 }} />
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: palette.textDark, mb: 0.5 }}>
                  {t('help.response_time')}
                </Typography>
                <Typography variant="body2" sx={{ color: palette.textMuted }}>
                  {t('help.response_time_value')}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ bgcolor: palette.bgDark, color: 'white', py: { xs: 8, md: 10 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
            {t('help.cta_title')}
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.7, fontWeight: 400 }}>
            {t('help.cta_subtitle')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/apply')}
              endIcon={<ArrowForwardIcon />}
              sx={{
                bgcolor: palette.accent,
                color: palette.bgDark,
                px: 5,
                py: 1.5,
                fontSize: '1.05rem',
                fontWeight: 700,
                borderRadius: 2,
                '&:hover': { bgcolor: '#c4963e' },
              }}
            >
              {t('help.cta_start_trial')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/')}
              sx={{
                borderColor: 'rgba(255,255,255,0.25)',
                color: 'white',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.5)',
                  bgcolor: 'rgba(255,255,255,0.05)',
                },
              }}
            >
              {t('help.back_home')}
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: palette.bgDark, borderTop: '1px solid rgba(255,255,255,0.06)', py: 3 }}>
        <Container maxWidth="lg">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)' }}>
              © 2026 URStore. All rights reserved.
            </Typography>
            <Button
              size="small"
              onClick={() => navigate('/')}
              sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}
            >
              {t('help.back_home')}
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

export default Help;
